import { CollectionOrder } from '../models/common/CollectionOrder';
import { isNullOrUndefined, isNullOrEmpty } from './typeChecks';
import { 
	months3char,
	monthsFull,
	weekdays2char,
	weekdays3char,
	weekdaysFull,
	meridiemUppercase,
	meridiemLowercase,
	meridiemFull,
	translateArray,
} from './date';
import { TTranslateFn } from '../models/common';

function defaultGetKeyFn<T>(item: T) {
	return item ? item.toString() : '';
}

export function filterOutEmpty<T>(collection: T[], getValueFn?: (T) => any): T[] {
	if (!getValueFn) {
		getValueFn = (item: T) => item;
	}
	return collection.filter(item => !isNullOrEmpty(getValueFn(item)));
}

export function findWithMatchingProperties<T>(collection: T[], matchingProperties: Partial<T>): T | undefined {
	return (collection || []).find((item) => {
		return Object.keys(matchingProperties).every(key => item[key] === matchingProperties[key]);
	});
}

export function filterWithMatchingProperties<T>(collection: T[], matchingProperties: Partial<T>): T[] | undefined {
	return (collection || []).filter((item) => {
		return Object.keys(matchingProperties).every(key => item[key] === matchingProperties[key]);
	});
}

export function pivotCollection<T>(collection: T[], getKeyFn: (value: T) => string, getValueFn: (value: T) => any = (value => value)): { [key: string]: T } {
	return (collection || []).reduce((acc, element) => {
		const key = getKeyFn(element);
		acc[key] = getValueFn(element);
		return acc;
	}, {});
}

export function sortBy<T>(collection: T[], getValueFn: (value: T) => string | number, order: CollectionOrder = 'ASC', culture?: string): T[] {
	return (collection || []).sort((a, b) => {
		const aValue = getValueFn(a);
		const bValue = getValueFn(b);
		let response;
		if (typeof aValue === 'number' && typeof bValue === 'number') {
			response = aValue > bValue ? 1 : -1;
		}
		else {
			response = aValue.toString().localeCompare(bValue.toString(), culture);
		}

		if (order === 'DESC') {
			response = response * -1;
		}
		return response;
	});
}

 export enum SortType {
	months3char = 'months3char',
	monthsFull = 'monthsFull',
	weekdays2char ='weekdays2char',
	weekdays3char = 'weekdays3char',
	weekdaysFull ='weekdaysFull',
	meridiemUppercase ='meridiemUppercase',
	meridiemLowercase = 'meridiemLowercase',
	meridiemFull = 'meridiemFull',
	custom = 'custom', // A customComparisonList must be supplied to use the custom type
	default = 'default',
 };

 export interface ISortByTypeOptions {
	sortType?: SortType;
	order?: CollectionOrder; 
	ignoreCase?: boolean;
	culture?: string;
	translate?: TTranslateFn;
	customComparisonList?: string[];
 };

export function sortByType<T>(collection: T[], getValueFn: (value: T) => string, options?: ISortByTypeOptions): T[] {
	const { sortType = 'default', order = 'ASC', ignoreCase = false, culture, translate, customComparisonList = [] } = options;
	if (sortType === 'default') {
		return sortBy(collection, getValueFn, order, culture);
	}
	
	let comparisonList = sortType === 'custom' ? customComparisonList : getComparisonList(sortType);
	if (translate) {
		translateArray(translate, comparisonList);
	}
	if (ignoreCase) {
		comparisonList = comparisonList.map(item => item = item.toLocaleLowerCase(culture));
	}

	// Any values in "collection" that are not part of the known value list will be returned at the end of the response list
	let validCollection = [];
	let invalidCollection = [];

	collection.forEach(item => {
		let value = getValueFn(item);
		if (ignoreCase) {
			value = value.toLocaleLowerCase(culture);
		}
		if (comparisonList.includes(value)) {
			validCollection.push(item);
		}
		else {
			invalidCollection.push(item);
		}
	});

	const getKnownValueFn = (item: T) => {
		let value = getValueFn(item);
		if (ignoreCase) {
			value = value.toLocaleLowerCase(culture);
		}
		return comparisonList.indexOf(value);
	}
	const sortedValidCollection = sortBy(validCollection, getKnownValueFn, order, culture);
	return [...sortedValidCollection, ...invalidCollection];
}

function getComparisonList(knownType: SortType): string[] {
	switch (knownType) {
		case SortType.months3char: return months3char;
		case SortType.monthsFull: return monthsFull;
		case SortType.weekdays2char: return weekdays2char;
		case SortType.weekdays3char: return weekdays3char;
		case SortType.weekdaysFull: return weekdaysFull;
		case SortType.meridiemUppercase: return meridiemUppercase;
		case SortType.meridiemLowercase: return meridiemLowercase;
		case SortType.meridiemFull: return meridiemFull;
		default: return [];
	}	
}

export function sortByDate<T>(collection: T[], getStringDateFn: (value: T) => string, order: CollectionOrder = 'ASC'): T[] {
	if (collection) {
		let sorted = collection.map((item) => {
			const stringDateValue = getStringDateFn(item);
			return { ...item, _sortDate: stringDateValue ? new Date(stringDateValue).getTime() : 0 };
		});
		sorted = sorted.sort((a, b) => {
			let result = a._sortDate - b._sortDate;
			if (order === 'DESC') {
				result = result * -1;
			}
			return result;
		});
		return sorted.map(({ _sortDate, ...props }) => props as any as T);
	}
	return collection;
}

function mapToObject<T>(collection: T[], getKeyFn: (item: T) => string) {
	const mappedObject: { [id: string]: T; } = {};
	for (const item of collection) {
		const itemKey = getKeyFn(item);
		mappedObject[itemKey] = item;
	}
	return mappedObject;
}

export function union<T>(first: T[], second: T[], getKeyFn?: (item: T) => string): T[] {
	if (isNullOrUndefined(first) || isNullOrUndefined(second)) {
		return null;
	}

	if (!getKeyFn) {
		getKeyFn = defaultGetKeyFn;
	}

	const mappedObject = mapToObject([...first, ...second], getKeyFn);

	const result = Object.keys(mappedObject).map((e: any) => {
		return mappedObject[e];
	});

	return result;
}

export function intersect<T>(first: T[], second: T[], getKeyFn?: (item: T) => string): T[] {
	if (isNullOrUndefined(first) || isNullOrUndefined(second)) {
		return null;
	}

	if (!getKeyFn) {
		getKeyFn = defaultGetKeyFn;
	}

	const mappedObject = mapToObject(second, getKeyFn);

	const result = first.filter((item) => {
		const itemKey = getKeyFn(item);
		return mappedObject[itemKey];
	});
	return result;
}

export function except<T>(first: T[], second: T[], getKeyFn?: (item: T) => string): T[] {
	if (isNullOrUndefined(first) || isNullOrUndefined(second)) {
		return null;
	}

	if (!getKeyFn) {
		getKeyFn = defaultGetKeyFn;
	}

	const mappedObject = mapToObject(second, getKeyFn);

	const result = first.filter((item) => {
		const itemKey = getKeyFn(item);
		return !mappedObject[itemKey];
	});
	return result;
}

export function groupBy<T>(collection: T[], getValueFn: (value: T) => string) {
	return (collection || []).reduce((acc, obj) => {
		const value = getValueFn(obj);
		acc[value] = (acc[value] || []).concat(obj);
		return acc;
	}, {});
}

export function split<T>(collection: T[], itemsPerRow: number = 1, isBreakpoint?: (element: T) => boolean): T[][] {
	if (isNullOrUndefined(collection)) {
		return [];
	}
	if (!isBreakpoint) {
		isBreakpoint = (element: T) => {
			return false;
		};
	}
	if (isNullOrUndefined(itemsPerRow) || itemsPerRow < 1) {
		itemsPerRow = 1;
	}
	const rows: T[][] = [];
	let currentRow: T[] = [];
	let count: number = 0;

	for (const element of collection) {
		if (count === itemsPerRow || isBreakpoint(element)) {
			rows.push(currentRow);
			currentRow = [];
			count = 0;
		}
		currentRow.push(element);
		count++;
	}
	rows.push(currentRow);
	return rows;
}

export function unique<T>(collection: T[], getValueFn: (value: T) => string) {
	if (isNullOrUndefined(collection)) {
		return [];
	}

	// get keys to determine unique collection
	const keys = collection.map(obj => getValueFn(obj));
	// filter collection to bring back a unique list based on the keys
	const uniqueCollection = collection.filter((obj, pos, arr) => {
		// indexOf returns the index of the first occurrence, using this logic,
		// we can filter out the duplicates that don't match the with the first occurrence therefore giving us a unique list.
		const value = getValueFn(obj);
		return keys.indexOf(value) === pos;
	});
	return uniqueCollection;
}

/**
 * Used to flatten multidimensional arrays. Not needed with ES2020 (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat)
 * @param arr 
 * @param depth pass `infinity` to flatten all levels of arrays
 * @returns array
 */
export function flatDeep(arr: any[], depth: number = 1) {
	if (arr) {
		return depth > 0 ?
			arr.reduce((acc, val) => acc.concat(Array.isArray(val) ?
				flatDeep(val, depth - 1) :
				val),
				[])
			: arr.slice();
	}
	return null;
}